import get from 'lodash/get';

export const AWARD_FULL_TUITION = 'full-tuition';
export const AWARD_RANGE = 'range';
export const AWARD_FIXED = 'fixed-amount';

export const stateAbbreviationToNameMap = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  DC: 'District of Columbia',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
  UNKNOWN: 'UNKNOWN',
};

export const calculatePercentWidthForAvg = (
  costsData,
  tuitionStatistics,
  type,
  amountType,
) => {
  const aid = get(costsData, `averageAmountOfAid.${type}.value`, 0);
  const min = get(tuitionStatistics, `FOUR_YEAR.MINIMUMS.${amountType}`, 0);
  const max = get(tuitionStatistics, `FOUR_YEAR.MAXIMUMS.${amountType}`, 0);

  return ((aid - min) / (max - min)) * 100;
};

export const calculatePercentWidthForNationalAvg = (tuitionStatistics, amountType) => {
  const avg = get(tuitionStatistics, `FOUR_YEAR.AVERAGES.${amountType}`, 0);
  const min = get(tuitionStatistics, `FOUR_YEAR.MINIMUMS.${amountType}`, 0);
  const max = get(tuitionStatistics, `FOUR_YEAR.MAXIMUMS.${amountType}`, 0);

  return ((avg - min) / (max - min)) * 100;
};

const getHighestAwardAmount = ({ awardType, awAmountMin, awAmountMax }) =>
  awardType === AWARD_RANGE ? awAmountMax : awAmountMin;

export const sortScholarships = (scholarships) => {
  return scholarships.sort((first, second) => {
    if (first.awardType === 'full-tuition') return -1;
    if (second.awardType === 'full-tuition') return 1;
    const firstAward = getHighestAwardAmount(first);
    const secondAward = getHighestAwardAmount(second);
    return secondAward - firstAward;
  });
};

export const getLastUpdatedDate = (lastUpdated) => {
  /* istanbul ignore if */
  if (!lastUpdated) return 'Unknown';
  const lastUpdatedDate = new Date(lastUpdated * 1000);
  const lastUpdatedMonth = lastUpdatedDate.toLocaleString('en-US', { month: 'long' });
  const lastUpdatedYear = lastUpdatedDate.getFullYear();
  return `${lastUpdatedMonth}, ${lastUpdatedYear}`;
};

const criteriaCanBeIgnored = (value) => value === undefined || Number(value) === 0;

/*
  Returns:
    1  if GPA meets criteria
    0  if GPA criteria can be ignored
    -1 if GPA fails to meet criteria
 */
const gpaMatch = (gpa, { gpaMin, gpaMax }) => {
  if (criteriaCanBeIgnored(gpaMin)) return 0;
  if (!gpa || gpa < Number(gpaMin)) return -1;
  return criteriaCanBeIgnored(gpaMax) || gpa <= Number(gpaMax) ? 1 : -1;
};

/*
  Returns:
    1  if Class Rank meets criteria
    0  if Class Rank criteria can be ignored
    -1 if Class Rank fails to meet criteria
 */
const classRankMatch = (classRank, { classRankMin, classRankMax }) => {
  if (criteriaCanBeIgnored(classRankMax)) return 0;
  if (classRank === undefined || classRank > Number(classRankMax)) return -1;
  return criteriaCanBeIgnored(classRankMin) || classRank >= Number(classRankMin) ? 1 : -1;
};

/*
  Returns:
    1  if SAT meets criteria
    0  if SAT criteria can be ignored
    -1 if SAT fails to meet criteria
 */
const satMatch = (sat, { satMin, satMax }) => {
  if (criteriaCanBeIgnored(satMin)) return 0;
  if (sat === undefined || sat < Number(satMin)) return -1;
  return criteriaCanBeIgnored(satMax) || sat <= Number(satMax) ? 1 : -1;
};

/*
  Returns:
     1 if ACT meets criteria
     0 if ACT criteria can be ignored
    -1 if ACT fails to meet criteria
 */
const actMatch = (act, { actMin, actMax }) => {
  if (criteriaCanBeIgnored(actMin)) return 0;
  if (act === undefined || act < Number(actMin)) return -1;
  return criteriaCanBeIgnored(actMax) || act <= Number(actMax) ? 1 : -1;
};

/*
  If gpaRankMatch === 'AND', this function Returns
    true if gpaMatchScore + classRankMatchScore > 0 or if both gpaMatchScore and classRankMatchScore === 0
    false if either gpaMatchScore or classRankMatchScore === -1
  If gpaRankMatch === 'OR', this function Returns
    true if gpaMatchScore + classRankMatchScore > -1
    false if gpaMatchScore + classRankMatchScore < 0
 */
const gpaClassRankMatch = (gpaMatchScore, classRankMatchScore, { gpaRankMatch }) => {
  const gpaPlusClassRank = gpaMatchScore + classRankMatchScore;
  return gpaRankMatch === 'and'
    ? gpaPlusClassRank > 0 || (gpaMatchScore === 0 && classRankMatchScore === 0)
    : gpaPlusClassRank > -1;
};

/*
  If satActMatch === 'AND', this function Returns
    true if satMatchScore + actMatchScore > 0 or if both satMatchScore and actMatchScore === 0
    false if either satMatchScore or actMatchScore === -1
  If satActMatch === 'OR', this function Returns
    true if satMatchScore + actMatchScore > -1
    false if satMatchScore + actMatchScore < 0
 */
const satActMatch = (satMatchScore, actMatchScore, { satActMatch }) => {
  const satPlusAct = satMatchScore + actMatchScore;
  return satActMatch === 'and'
    ? satPlusAct > 0 || (satMatchScore === 0 && actMatchScore === 0)
    : satPlusAct > -1;
};

const overallMatch = (gpaClassRankMatch, satActMatch, { satGpaMatch }) => {
  return satGpaMatch === 'and'
    ? gpaClassRankMatch && satActMatch
    : gpaClassRankMatch || satActMatch;
};

const isAcademicsMatch = (scholarship, gpa, sat, act, classRank) => {
  const gpaMatchScore = gpaMatch(gpa, scholarship);
  const classRankMatchScore = classRankMatch(classRank, scholarship);
  const satMatchScore = satMatch(sat, scholarship);
  const actMatchScore = actMatch(act, scholarship);
  const gpaClassRankMatched = gpaClassRankMatch(
    gpaMatchScore,
    classRankMatchScore,
    scholarship,
  );
  const satActMatched = satActMatch(satMatchScore, actMatchScore, scholarship);
  return overallMatch(gpaClassRankMatched, satActMatched, scholarship);
};

export const isLocationMatch = (state, scholarship) =>
  get(scholarship, 'audienceStates', []).includes(state);

const getMaxAmount = ({ awAmountMax, awAmountMin }) => {
  const maxAmount = awAmountMax && !Number.isNaN(awAmountMax) ? awAmountMax : 0;
  const minAmount = awAmountMin && !Number.isNaN(awAmountMin) ? awAmountMin : 0;
  return Math.max(maxAmount, minAmount);
};
export const findBestMatch = (scholarships, studentProfile) => {
  const { gpa, sat, act, classRank, state: studentState } = studentProfile;
  const state = studentState.toUpperCase();
  const matchingScholarships = scholarships.filter(
    (schlrshp) =>
      isLocationMatch(state, schlrshp) &&
      isAcademicsMatch(schlrshp, gpa, sat, act, classRank),
  );
  if (matchingScholarships.length === 1) {
    return matchingScholarships[0];
  }
  let bestMatch = matchingScholarships.find(
    (eachScholarship) => AWARD_FULL_TUITION === eachScholarship.awardType,
  );
  /* istanbul ignore else */
  if (!bestMatch) {
    matchingScholarships.sort((a, b) => getMaxAmount(b) - getMaxAmount(a));
    bestMatch = matchingScholarships[0];
  }
  return bestMatch;
};

export default findBestMatch;
